import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { ChevronRight, Radar } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { userCanPlatformAny, type StaffUser } from "../../lib/auth";
import { CRM_NAV, isNavActive, isRouteActive, type CrmNavItem } from "../nav";
import { JobsStack, railBusy } from "./jobs";
import { RailPins } from "./RailPins";
import { useSignals } from "./SignalsProvider";

/** Where the counter shown on a nav item should read as urgent rather than
 *  merely informational. Unread mail and missed calls are somebody waiting for
 *  an answer; a task count is a backlog. An overdue follow-up belongs with the
 *  first pair, not the second: the date came out of a promise made to somebody
 *  on the phone, and the counter only ever holds the ones already past it. A
 *  customer site that is down is the most urgent of all of them: somebody is
 *  losing visitors while the number stands. */
const HOT: Partial<Record<string, boolean>> = { mailUnread: true, callsMissed: true, brainDue: true, sitesDown: true };

/**
 * The one sidebar, in two states.
 *
 * Collapsed it is a rail of 44px icon pills, not a narrower version of the same
 * list: the icons keep the same x they had when expanded, so collapsing only
 * drops the labels instead of sliding the whole column sideways. The state is
 * a class on <html> rather than React state, because the header logo and the
 * content grid have to move with it and neither is inside this component.
 *
 * At drawer widths this same markup slides in over the page instead of standing
 * next to it (see index.css, `max-width: 760px`). It is the same list rather
 * than a phone-only copy, so a nav item can never exist in one and be missing
 * from the other. `onNavigate` is the drawer's close signal - the layout also
 * closes on a route change, but tapping the row you are already on has no route
 * change to react to.
 */
export function Rail({
  user,
  collapsed,
  onToggle,
  open = false,
  onNavigate,
}: {
  user: StaffUser;
  collapsed: boolean;
  onToggle: () => void;
  open?: boolean;
  onNavigate?: () => void;
}) {
  const t = useT();
  const { pathname } = useLocation();
  const { badges, jobs, attention, beats } = useSignals();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ top: false, bottom: false });
  const [tip, setTip] = useState<{ label: string; x: number; y: number } | null>(null);
  // Groups follow the route by default; an explicit toggle overrides that for
  // the rest of the session.
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>({});

  // The mask only opens on the side that still has something to scroll to: a
  // permanently faded top edge would read as a rendering fault.
  const updateFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setFade({
      top: el.scrollTop > 1,
      bottom: el.scrollTop + el.clientHeight < el.scrollHeight - 1,
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateFades();
    const ro = new ResizeObserver(updateFades);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateFades]);

  const showTip = (e: React.MouseEvent<HTMLElement>, label: string) => {
    if (!collapsed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ label, x: Math.round(r.right + 12), y: Math.round(r.top + r.height / 2) });
  };

  const groups = CRM_NAV.map((g) => ({
    ...g,
    items: g.items.filter((i) => userCanPlatformAny(user, i.perm)),
  })).filter((g) => g.items.length > 0);

  const renderItem = (item: CrmNavItem) => {
    const Icon = item.icon;
    const label = t(item.labelKey);
    const count = item.badge ? badges[item.badge] : undefined;
    const active = isNavActive(pathname, item);
    // A group stands open while the route is inside it, and stays open once it
    // has been opened by hand. Collapsing the rail hides the children entirely -
    // an indented pill next to a 44px icon has nowhere to indent to.
    const open = item.children?.length ? (groupOpen[item.path] ?? active) && !collapsed : false;

    return (
      <Fragment key={item.path}>
        <div className="relative flex items-center">
          {/* A plain `Link` with `aria-current` from `isNavActive`, not a
              `NavLink`. NavLink decides WHEN a row is the current page by its
              own prefix match and lets the caller choose only the value, so
              on `/prospects/follow-up` the Kliendiotsing row (`/prospects`)
              was read out as the current page beside the follow-up row that
              is actually lit, the overview row likewise under every
              `/marketing/...` page, and a row lit by `match` alone (Campaigns
              on `/marketing/automation`) was never announced at all. The look
              and the announcement now come from one answer. */}
          <Link
            to={item.path}
            aria-current={active ? "page" : undefined}
            className={cn("crm-nav min-w-0 flex-1", active && "is-on", count ? "has-badge" : "")}
            onMouseEnter={(e) => showTip(e, label)}
            onMouseLeave={() => setTip(null)}
            onClick={() => {
              setTip(null);
              onNavigate?.();
            }}
          >
            <Icon className="size-[18px]" strokeWidth={1.7} />
            <span className="crm-nav-l">{label}</span>
            {count ? <span className={cn("crm-n", HOT[item.badge!] && "crm-n-hot")}>{count}</span> : null}
          </Link>
          {item.children?.length && !collapsed ? (
            <button
              type="button"
              className="crm-nav-toggle"
              aria-expanded={open}
              aria-label={label}
              onClick={() => setGroupOpen((prev) => ({ ...prev, [item.path]: !open }))}
            >
              <ChevronRight className={cn("size-3.5 transition-transform", open && "rotate-90")} />
            </button>
          ) : null}
        </div>

        {open
          ? item.children!.map((child) => {
              const ChildIcon = child.icon;
              return (
                <NavLink
                  key={child.path}
                  to={child.path}
                  className={cn("crm-nav crm-nav-child", isRouteActive(pathname, child.path) && "is-on")}
                  onClick={() => onNavigate?.()}
                >
                  <ChildIcon className="size-4" strokeWidth={1.7} />
                  <span className="crm-nav-l">{t(child.labelKey)}</span>
                </NavLink>
              );
            })
          : null}
      </Fragment>
    );
  };

  return (
    <aside id="crm-nav" className={cn("crm-side", open && "is-open")} aria-label={t("app.nav")}>
      <div className="crm-side-wrap">
        {/* OUTSIDE the scroll box, and that is the whole point: the pins are a
            shortcut, and a shortcut that scrolls away with the nav is one you
            have to go looking for. Sitting here they hold the top of the rail on
            every page, whichever section the operator has scrolled down to.

            Not `position: sticky` inside the scroll box, which is the obvious
            build and would have been wrong: that box is masked at both ends to
            fade what it is scrolling past, and a mask applies to the element's
            own box rather than to its content - so a pinned row parked in the
            top 46px would have faded out along with the rows it was meant to
            stand in front of.

            The block draws nothing when nothing is pinned (`RailPins` returns
            null), so the CSS hides the empty wrapper with `:empty` rather than
            this component having to know. */}
        <div className="crm-side-pins">
          <RailPins
            user={user}
            collapsed={collapsed}
            onTip={showTip}
            onTipOut={() => setTip(null)}
            onNavigate={onNavigate}
          />
        </div>

        <div
          ref={scrollRef}
          className={cn("crm-side-scroll", fade.top && "is-ftop", fade.bottom && "is-fbot")}
          onScroll={() => {
            updateFades();
            setTip(null);
          }}
        >
          {/* Fragments, not wrappers: the collapsed rail lays these out as flex
              items, and a wrapper would become the item instead of the pills. */}
          {groups.map((group) => (
            <Fragment key={group.titleKey}>
              <div className="crm-sect">{t(group.titleKey)}</div>
              {group.items.map(renderItem)}
            </Fragment>
          ))}
        </div>
      </div>

      {/* Collapsed, the card is gone but the fact that something is running is
          not - it survives as one dot on a single icon button. */}
      <div className="crm-side-foot">
        <JobsStack />
        <button type="button" className="crm-jobs-mini" title={t("jobs.title")} aria-label={t("jobs.title")}>
          <Radar className="size-[18px]" strokeWidth={1.7} />
          {/* Trouble deserves the dot as much as work does: an operator who
              collapsed the rail did not ask to stop being told things. */}
          {railBusy({ jobs, attention, beats }) ? <i className="crm-run-dot" /> : null}
        </button>
      </div>

      <button
        type="button"
        className="crm-handle"
        onClick={() => {
          setTip(null);
          onToggle();
        }}
        aria-label={t(collapsed ? "app.railExpand" : "app.railCollapse")}
        title={t(collapsed ? "app.railExpand" : "app.railCollapse")}
      >
        <span className="crm-handle-line" />
      </button>

      {/* The tooltip is a body-level layer: the rail's scroll box would clip a
          child of the nav item in half. */}
      {tip
        ? createPortal(
            <div
              className="crm-tip is-on"
              role="tooltip"
              style={{ left: tip.x, top: tip.y, transform: "translateY(-50%)" }}
            >
              {tip.label}
            </div>,
            document.body,
          )
        : null}
    </aside>
  );
}
